import { CheckboxInput, EmailInput, PasswordInput } from '@frontend/components/basic-forms';
import { CommonMessage } from '@frontend/lang';
import { useBrandingRepository } from '@frontend/repository';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import defaultLogo from '../../../../assets/images/telloport_logo_horizontal.png';
import { VERSION_NUMBER } from '../../../../version';
import Sso from '../../sso/sso.component';
import useBasicLogin from './basic-login.controller';

export const ID = 'basic-login';
export interface Props {
    callback?: () => void;
}

const BasicLogin = (props: Props) => {
    const viewProps = useBasicLogin(props);
    const { logo_navbar_expanded } = useBrandingRepository();

    const logo = useMemo(() => {
        if (logo_navbar_expanded) return logo_navbar_expanded;
        return defaultLogo;
    }, [logo_navbar_expanded]);
    const handlePostLogin = () => {
        props.callback && props.callback();
        const redirectTo = sessionStorage.getItem('preLoginRedirect');
        if (redirectTo) {
            sessionStorage.removeItem('preLoginRedirect');
            viewProps.navigate(redirectTo);
        }
    };

    return (
        <div
            id={ID}
            className='relative flex h-screen w-screen flex-col'>
            <div className='bg-brand-structure absolute top-0 right-0 left-0 -z-1 h-[40vh] w-full'></div>
            <form
                className='bg-background-surface mx-6 my-40 flex flex-col items-center gap-6 rounded-lg p-6 shadow-lg md:mx-auto md:w-1/2'
                onSubmit={viewProps.onSubmit}>
                <img
                    src={logo}
                    className='img-fluid mx-4 my-12 w-44'
                    alt='Logo'
                />

                <div className='flex w-full flex-col gap-4'>
                    <h1 className='text-foreground-default text-2xl font-medium'>{CommonMessage.BUTTONS.LOGIN}</h1>
                    <EmailInput
                        required
                        label='Email'
                        errorMessage='Please fill in a valid email.'
                        autoFocus
                        value={viewProps.formValues.email}
                        onChange={(value) => viewProps.changeFormValues({ ...viewProps.formValues, email: value })}
                        isValidCallback={(value) => {
                            if (viewProps.submitted) viewProps.changeEmailValid(value);
                        }}
                        submitted={viewProps.submitted}
                        valid={viewProps.emailValid}
                        noConditionedStyling
                    />
                    <PasswordInput
                        required
                        label='Password'
                        errorMessage='Please fill in your password.'
                        value={viewProps.formValues.password}
                        onChange={(value) => viewProps.changeFormValues({ ...viewProps.formValues, password: value })}
                        isValidCallback={(value) => {
                            if (viewProps.submitted) viewProps.changePasswordValid(value);
                        }}
                        noConditionedStyling
                        valid={viewProps.passwordValid}
                    />
                    <CheckboxInput
                        id='keep-logged-in'
                        value={viewProps.keepLoggedIn}
                        onChange={viewProps.changeKeepLoggedIn}
                        label={
                            <FormattedMessage
                                id='login.keep-me-logged-in'
                                description='Checkbox label on the login page to enable automatic session token refresh'
                                defaultMessage='Keep me logged in'
                            />
                        }
                    />
                </div>

                <div className='flex w-full flex-col content-center items-center gap-3'>
                    <Sso
                        callback={handlePostLogin}
                        keepLoggedIn={viewProps.keepLoggedIn}
                    />
                    <button
                        id='submit-button'
                        type='submit'
                        data-testid='signInButton'
                        className='bg-brand-primary hover:bg-brand-secondary inline-flex w-1/3 items-center justify-center gap-2.5 rounded-md p-3.5 hover:cursor-pointer'
                        disabled={
                            viewProps.formValues.email?.length === 0 ||
                            viewProps.formValues.password?.length === 0 ||
                            !viewProps.emailValid ||
                            !viewProps.passwordValid ||
                            viewProps.submitted
                        }>
                        <div className='text-background-surface justify-start text-base font-medium'>{CommonMessage.BUTTONS.LOGIN}</div>
                    </button>
                    <span
                        id='forgot-password-button'
                        onClick={() => viewProps.navigate('/forgot-password')}
                        data-testid='forgotPasswordLink'
                        className='text-brand-muted cursor-pointer font-medium hover:underline'>
                        <FormattedMessage
                            id='forgotPassword'
                            defaultMessage='Forgot password'
                        />
                    </span>
                </div>
            </form>
            <div className='absolute right-0 bottom-4 left-0 flex justify-center'>
                <span className='text-foreground-muted font-mono text-xs'>{VERSION_NUMBER}</span>
            </div>
        </div>
    );
};

export default BasicLogin;
